/**
 * useRuleComplianceStats Hook
 *
 * Fetches and transforms rule compliance statistics.
 * Also fetches the full rules list and merges them, ensuring rules
 * without audit events are still displayed with zero counts.
 *
 * Supports both provider and admin endpoints via organizationId parameter.
 */

import { useCallback,useMemo } from 'react';
import { useGetAdminRulesQuery } from '@features/rules/store/adminApi';
import { useGetRulesQuery } from '@features/rules/store/api';
import { skipToken } from '@reduxjs/toolkit/query';
import type { ProviderRuleComplianceResponse, RuleComplianceStatsResponse } from '@store/generated/jotaudit';

import { useGetAdminRuleComplianceStatsQuery } from '../store/adminApi';
import { useGetRuleComplianceStatsQuery } from '../store/api';

interface UseRuleComplianceStatsParams {
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Period in days */
  days?: number;
  /** Start date (ISO format) */
  dateFrom?: string;
  /** End date (ISO format) */
  dateTo?: string;
  /** Comma-separated provider IDs */
  userIds?: string;
  /** Comma-separated rule IDs */
  ruleIds?: string;
  /** Enable comparison mode to get by_provider breakdown */
  comparisonMode?: boolean;
}

interface UseRuleComplianceStatsResult {
  /** Array of rule compliance stats (includes rules without audits) */
  rules: RuleComplianceStatsResponse[];
  /** Most violated rule */
  mostViolatedRule: RuleComplianceStatsResponse | null;
  /** Most compliant rule */
  mostCompliantRule: RuleComplianceStatsResponse | null;
  /** Total number of rules */
  totalRules: number;
  /** Period in days if filtered */
  periodDays: number | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Overall compliance rate (average across rules with audits) */
  overallComplianceRate: number | null;
  /** Provider breakdown when comparison mode is enabled */
  byProvider?: ProviderRuleComplianceResponse[];
}

/**
 * Helper to get rule title from a rule item
 * Works with both provider and admin rule response types
 */
const getRuleTitle = (rule: { rule: { title?: string | null } }): string => {
  return rule.rule?.title || 'Untitled Rule';
};

/**
 * Type for normalized rule item (common fields between provider and admin)
 */
interface NormalizedRuleItem {
  rule_id: string;
  is_disabled?: boolean;
  rule: { title?: string | null };
}

export const useRuleComplianceStats = (
  params?: UseRuleComplianceStatsParams,
): UseRuleComplianceStatsResult => {
  const filterParams = params
    ? {
        ...(params.days !== undefined && { days: params.days }),
        ...(params.dateFrom && { dateFrom: params.dateFrom }),
        ...(params.dateTo && { dateTo: params.dateTo }),
        ...(params.userIds && { userIds: params.userIds }),
        ...(params.ruleIds && { ruleIds: params.ruleIds }),
        ...(params.comparisonMode !== undefined && { comparisonMode: params.comparisonMode }),
      }
    : {};

  // ========== COMPLIANCE STATS QUERIES ==========
  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminStatsQuery = useGetAdminRuleComplianceStatsQuery(
    params?.organizationId
      ? {
          organizationId: params.organizationId,
          ...filterParams,
        }
      : skipToken,
  );

  const providerStatsQuery = useGetRuleComplianceStatsQuery(
    !params?.organizationId ? filterParams : skipToken,
  );

  // Use whichever stats query is active
  const statsQuery = params?.organizationId ? adminStatsQuery : providerStatsQuery;

  // ========== RULES LIST QUERIES ==========
  // Fetch all compliance rules to include those without audit events
  const providerRulesQuery = useGetRulesQuery(
    !params?.organizationId
      ? {
          rule_type: 'compliance',
          scope: 'both',
          per_page: 500, // Fetch all rules
          include_disabled: true,
        }
      : skipToken,
  );

  const adminRulesQuery = useGetAdminRulesQuery(
    params?.organizationId
      ? {
          organizationId: params.organizationId,
          ruleType: 'compliance',
          scope: 'both',
          perPage: 500, // Fetch all rules
        }
      : skipToken,
  );

  // Use whichever rules query is active
  const rulesQuery = params?.organizationId ? adminRulesQuery : providerRulesQuery;

  // Combined loading state - wait for both queries
  const isLoading = statsQuery.isLoading || rulesQuery.isLoading;

  // Combined error state
  const error = statsQuery.error || rulesQuery.error;

  // Combined refetch
  const refetch = useCallback(() => {
    statsQuery.refetch();
    rulesQuery.refetch();
  }, [statsQuery, rulesQuery]);

  // ========== MERGE DATA ==========
  const result = useMemo(() => {
    // If stats are still loading, return empty state
    if (!statsQuery.data && !rulesQuery.data) {
      return {
        rules: [],
        mostViolatedRule: null,
        mostCompliantRule: null,
        totalRules: 0,
        periodDays: null,
        overallComplianceRate: null,
      };
    }

    const statsData = statsQuery.data;
    const rulesData = rulesQuery.data;

    // Get stats from the compliance stats endpoint
    const statsRules = statsData?.rules || [];
    const mostViolatedRule = statsData?.most_violated_rule || null;
    const mostCompliantRule = statsData?.most_compliant_rule || null;
    const periodDays = statsData?.period_days || null;
    const byProvider = statsData?.by_provider || undefined;

    // Create a map of rule_id -> stats for quick lookup
    const statsMap = new Map<string, RuleComplianceStatsResponse>();
    for (const stat of statsRules) {
      statsMap.set(stat.rule_id, stat);
    }

    // Get all rules from the rules list endpoint
    // Cast to common type that works for both provider and admin responses
    const allRules = (rulesData?.items || []) as NormalizedRuleItem[];

    // Merge: include all rules, using stats if available, otherwise create empty stats
    const mergedRules: RuleComplianceStatsResponse[] = allRules.map((rule) => {
      const existingStats = statsMap.get(rule.rule_id);
      if (existingStats) {
        // Rule has audit data, use it
        return existingStats;
      }
      // Rule has no audit data, create empty stats entry
      return {
        rule_id: rule.rule_id,
        rule_name: getRuleTitle(rule),
        compliance_rate: undefined, // Will display as "—" in UI
        compliant_count: 0,
        non_compliant_count: 0,
        total_audits: 0,
        is_disabled: rule.is_disabled,
        is_deleted: false,
      };
    });

    // Also add any rules from stats that might not be in the rules list
    // (edge case: rule was deleted but still has historical stats)
    for (const stat of statsRules) {
      const existsInMerged = mergedRules.some(r => r.rule_id === stat.rule_id);
      if (!existsInMerged) {
        mergedRules.push(stat);
      }
    }

    // Total rules is the merged count
    const totalRules = mergedRules.length;

    // Calculate overall compliance rate only from rules that have audits
    const rulesWithAudits = mergedRules.filter(r => (r.total_audits ?? 0) > 0);
    const overallComplianceRate =
      rulesWithAudits.length > 0
        ? rulesWithAudits.reduce((sum, rule) => sum + (rule.compliance_rate || 0), 0) / rulesWithAudits.length
        : null;

    return {
      rules: mergedRules,
      mostViolatedRule,
      mostCompliantRule,
      totalRules,
      periodDays,
      overallComplianceRate,
      byProvider,
    };
  }, [statsQuery.data, rulesQuery.data]);

  return {
    ...result,
    isLoading,
    error: error ? 'Failed to load rule compliance statistics' : null,
    refetch,
  };
};

export default useRuleComplianceStats;

